import { useCallback, useMemo, useState } from "react";
import { Plus, FilePenLine, ShieldCheck, Users } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { useLingui } from "@lingui/react";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { SegmentedToggle } from "@/components/ui/segmented-toggle";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/table/EmptyState";
import { CreateChangeRequestDialog } from "@/components/children/CreateChangeRequestDialog";
import { ChildrenTable } from "@/components/table/ChildrenTable";
import { useChildrenPaginated, useUpdateChildField, type ChildrenPaginatedOptions } from "@/hooks/useChildren";
import { useCanOpen } from "@/hooks/useCanOpen";
import { useListUrlState } from "@/hooks/useListUrlState";
import { fetchAllPages } from "@/hooks/fetchAllPages";
import { OpenAPI } from "@/api/core/OpenAPI";
import { request as __request } from "@/api/core/request";
import type { children_ChildListResponse } from "@/api/models/children_ChildListResponse";
import type { models_Child } from "@/types";

const SCOPES = ["current", "all"] as const;
type ChildrenScope = (typeof SCOPES)[number];
// Only the filters the list endpoint applies; a chip that changes nothing would mislead.
const FILTER_KEYS = ["status", "labels"] as const;
const DEFAULT_SORT: SortingState = [{ id: "firstname", desc: false }];

// Map frontend column IDs to backend sort_by values
const SORT_COLUMN_MAP: Record<string, string> = {
  firstname: 'firstname',
  lastname: 'lastname',
  birthdate: 'birthdate',
  status: 'status',
  createdAt: 'created_at',
  childInternalId: 'child_internal_id',
};

const ChildrenPage = () => {
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const [showChangeRequest, setShowChangeRequest] = useState(false);
  const canOpen = useCanOpen();

  // Search, page, sort, scope and filters live in the URL, so Back from a
  // child returns to the same filtered page.
  const list = useListUrlState<ChildrenScope, (typeof FILTER_KEYS)[number]>({
    scopes: SCOPES,
    defaultScope: "current",
    filterKeys: FILTER_KEYS,
    defaultSort: DEFAULT_SORT,
  });

  const sortEntry = list.sort[0];
  const queryOptions: ChildrenPaginatedOptions = useMemo(() => ({
    status: list.filters.status?.join(","),
    labelIds: list.filters.labels?.join(","),
    search: list.search || undefined,
    sortBy: (sortEntry && SORT_COLUMN_MAP[sortEntry.id]) || "firstname",
    sortOrder: sortEntry?.desc ? "desc" : "asc",
    withContracts: list.scope === "current",
  }), [list.filters.status, list.filters.labels, list.search, sortEntry, list.scope]);

  const {
    data: children,
    total,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useChildrenPaginated(list.pageSize, queryOptions, list.pagination);

  // The table seeds its filter chips from the URL once; `key={list.syncKey}`
  // re-mounts it when the URL changes from outside (sidebar, palette, Back).
  const initialFilters: ColumnFiltersState = FILTER_KEYS.flatMap((key) =>
    list.filters[key] ? [{ id: key, value: list.filters[key] }] : []
  );

  const { setFilters, setSort } = list;
  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    const values = (id: string) => {
      const value = filters.find((f) => f.id === id)?.value;
      return Array.isArray(value) && value.length > 0 ? (value as string[]) : undefined;
    };
    setFilters({ status: values("status"), labels: values("labels") });
  }, [setFilters]);

  // Columns the API cannot sort by are ignored rather than sorting one page.
  const handleSortingChange = useCallback((sorting: SortingState) => {
    const serverSortable = sorting.filter((s) => SORT_COLUMN_MAP[s.id]);
    setSort(serverSortable.length > 0 ? serverSortable : DEFAULT_SORT);
  }, [setSort]);

  // "Export all matching" pages through the same query the list shows.
  const exportAll = useCallback(
    () =>
      fetchAllPages<models_Child>(async (limit, offset) => {
        const response = await __request<children_ChildListResponse>(OpenAPI, {
          method: "GET",
          url: "/api/v1/children",
          query: {
            limit,
            offset,
            status: queryOptions.status,
            label_ids: queryOptions.labelIds,
            search: queryOptions.search,
            sort_by: queryOptions.sortBy,
            sort_order: queryOptions.sortOrder,
            with_contracts: queryOptions.withContracts || undefined,
          },
        });
        return response.children ?? [];
      }, 500),
    [queryOptions]
  );

  const updateChildFieldMutation = useUpdateChildField();

  const handleCellUpdate = async (
    rowId: string,
    field: string,
    newValue: models_Child[keyof models_Child]
  ) => {
    await updateChildFieldMutation.mutateAsync({
      id: rowId,
      field: field as keyof models_Child,
      value: newValue,
    });
  };

  const addChild = useCallback(() => navigate("/children/new"), [navigate]);
  const addChildLabel = i18n._({ id: "children.list.add", message: "Add child" });

  const emptyState = (
    <EmptyState
      icon={<Users className="h-10 w-10" />}
      title={
        list.scope === "current"
          ? i18n._({
              id: "children.list.emptyCurrent.title",
              message: "No children with a current or upcoming contract",
            })
          : i18n._({ id: "children.list.empty.title", message: "No children yet" })
      }
      action={
        <div className="flex flex-wrap justify-center gap-2">
          {list.scope === "current" && (
            <Button size="sm" variant="outline" onClick={() => list.setScope("all")}>
              {i18n._({ id: "children.list.showAll", message: "Show all children" })}
            </Button>
          )}
          <Button size="sm" onClick={addChild}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {addChildLabel}
          </Button>
        </div>
      }
    />
  );

  // The error shows inside the table body: a stale or shared URL filter the
  // API rejects stays visible in the toolbar and can be removed.
  const loadError =
    error && !isLoading ? (
      <QueryErrorState
        error={error}
        onRetry={() => void refetch()}
        action={
          list.isFiltered ? (
            <Button type="button" variant="outline" size="sm" onClick={list.reset}>
              {i18n._({ id: "table.filter.clearAll", message: "Clear all filters" })}
            </Button>
          ) : undefined
        }
      />
    ) : undefined;

  return (
    <PageContainer width="list" className="flex h-full flex-col">
      <PageHeader
        title={i18n._({ id: "nav.children", message: "Children" })}
        actions={
          <>
            {/* Admin only: the route guard and the server both refuse everyone else. */}
            {canOpen("/children/consents") && (
              <Button variant="outline" asChild>
                <Link to="/children/consents">
                  <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                  {i18n._({ id: "children.consents.overview.title", message: "Consent overview" })}
                </Link>
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowChangeRequest(true)}>
              <FilePenLine className="h-4 w-4" aria-hidden="true" />
              {i18n._({ id: "changeRequest.action.request", message: "Request data change" })}
            </Button>
            <Button onClick={addChild}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {addChildLabel}
            </Button>
          </>
        }
      />

      <CreateChangeRequestDialog
        open={showChangeRequest}
        onOpenChange={setShowChangeRequest}
      />

      <div className="min-h-0 flex-1">
        <ChildrenTable
          key={list.syncKey}
          data={loadError ? [] : children}
          isLoading={isLoading}
          errorState={loadError}
          onCellUpdate={handleCellUpdate}
          enableSelection={true}
          serverSidePagination={true}
          totalRows={loadError ? 0 : total}
          pagination={list.pagination}
          onPaginationChange={list.setPagination}
          isFetching={isFetching}
          initialFilters={initialFilters}
          onFilterChange={handleFilterChange}
          sorting={list.sort}
          onSortingChange={handleSortingChange}
          searchTerm={list.search}
          onSearchChange={list.setSearch}
          hasExternalFilters={list.search !== "" || Object.keys(list.filters).length > 0}
          exportAll={exportAll}
          emptyState={emptyState}
          toolbarRight={
            <SegmentedToggle<ChildrenScope>
              className="ml-auto"
              value={list.scope ?? "current"}
              onValueChange={list.setScope}
              aria-label={i18n._({ id: "children.filter.scopeLabel", message: "Which children" })}
              options={[
                { value: "current", label: i18n._({ id: "children.filter.current", message: "Current" }) },
                { value: "all", label: i18n._({ id: "children.filter.all", message: "All" }) },
              ]}
            />
          }
        />
      </div>
    </PageContainer>
  );
};

export default ChildrenPage;
